import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Pencil, Plus, Power, Truck } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { PanelDelProveedor } from './PanelDelProveedor';
import type { CatalogosDeProveedor, ListaDeProveedores, Proveedor } from './contrato';

export function PaginaProveedores() {
  const [texto, setTexto] = useState('');
  const [soloActivos, setSoloActivos] = useState(true);
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [editando, setEditando] = useState<Proveedor | null>(null);
  const [creando, setCreando] = useState(false);

  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeGestionar = puede('proveedores.gestionar');

  const buscado = useValorDiferido(texto.trim());

  const listado = useQuery({
    queryKey: ['proveedores', buscado, soloActivos, pagina, tamanio],
    queryFn: () =>
      api.get<ListaDeProveedores>('/proveedores', {
        query: { texto: buscado, activos: soloActivos ? 'true' : '', pagina, tamanio },
      }),
    placeholderData: (anterior) => anterior,
  });

  const catalogos = useQuery({
    queryKey: ['proveedores', 'catalogos'],
    queryFn: () => api.get<CatalogosDeProveedor>('/proveedores/catalogos'),
    staleTime: Infinity,
  });

  const cambiarEstado = useMutation({
    mutationFn: (p: Proveedor) =>
      api.post<Proveedor>(`/proveedores/${p.id}/activar`, { activo: !p.activo }),
    onSuccess: (p) => {
      notificar.exito(p.activo ? 'Proveedor reactivado' : 'Proveedor desactivado');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['proveedores'] });
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo cambiar'),
  });

  const columnas: Columna<Proveedor>[] = [
    {
      clave: 'proveedor',
      encabezado: 'Proveedor',
      contenido: (p) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-800">
            {p.razonSocial}
          </span>
          {p.nombreComercial && (
            <span className="block truncate text-[10px] uppercase text-armazon-400">
              {p.nombreComercial}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'identificacion',
      encabezado: 'Identificación',
      contenido: (p) => (
        <span className="block">
          <span className="importe block text-xs text-armazon-700">{p.identificacion}</span>
          <span className="block text-[10px] uppercase text-armazon-400">{p.tipoEtiqueta}</span>
        </span>
      ),
    },
    {
      clave: 'regimen',
      encabezado: 'Régimen',
      contenido: (p) => (
        <span className="block">
          <span className="block text-[11px] uppercase text-armazon-700">{p.regimenEtiqueta}</span>

          <span className="block text-[10px] uppercase text-armazon-400">
            {p.obligadoContabilidad ? 'Lleva contabilidad' : 'No lleva contabilidad'}
            {p.parteRelacionada && ' · parte relacionada'}
          </span>
        </span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      contenido: (p) => (
        <span className="block">
          <span
            className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${
              p.activo ? 'bg-marca-50 text-marca-700' : 'bg-armazon-100 text-armazon-500'
            }`}
          >
            {p.activo ? 'Activo' : 'Inactivo'}
          </span>
          {p.constaSuspendido && (
            <span className="mt-0.5 flex items-center justify-center gap-1 text-[10px] font-bold uppercase text-amber-700">
              <AlertTriangle className="size-3" aria-hidden />
              {p.estadoContribuyente} en el SRI
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-24',
      contenido: (p) =>
        puedeGestionar ? (
          <span className="flex justify-end gap-1">
            <BotonDeFila icono={Pencil} titulo="Editar" alPulsar={() => setEditando(p)} />
            <BotonDeFila
              icono={Power}
              titulo={p.activo ? 'Desactivar' : 'Reactivar'}
              alPulsar={() => cambiarEstado.mutate(p)}
            />
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Truck}
        color="indigo"
        titulo="Proveedores"
        subtitulo="A quién se le compra · su régimen decide cuánto se le retiene"
        acciones={
          puedeGestionar && (
            <button
              type="button"
              onClick={() => setCreando(true)}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Plus className="size-4" aria-hidden />
              Nuevo proveedor
            </button>
          )
        }
      />

      <Tabla
        datos={listado.data}
        cargando={listado.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="proveedores"
        iconoVacio={Truck}
        mensajeVacio="Ningún proveedor coincide"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <CampoDeBusqueda
              valor={texto}
              alCambiar={(v) => {
                setTexto(v);
                setPagina(0);
              }}
              marcador="Razón social, nombre comercial o identificación…"
              className="w-80"
            />
            <label className="flex items-center gap-2 text-[11px] font-semibold uppercase text-armazon-600">
              <input
                type="checkbox"
                checked={soloActivos}
                onChange={(e) => {
                  setSoloActivos(e.target.checked);
                  setPagina(0);
                }}
                className="size-4 rounded border-armazon-300"
              />
              Solo activos
            </label>
          </span>
        }
      />

      {(creando || editando) && (
        <PanelDelProveedor
          proveedor={editando}
          catalogos={catalogos.data}
          alCerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
          alGuardar={() => {
            setCreando(false);
            setEditando(null);
            void clienteDeConsultas.invalidateQueries({ queryKey: ['proveedores'] });
          }}
        />
      )}
    </>
  );
}
